import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useQueryClient } from "react-query";
import { useForm, Controller } from "react-hook-form";

import { InputNumber } from 'primereact/inputnumber';
import { InputText } from "primereact/inputtext";
import { Button } from "../../../../../../components/ui/Button";
import { COMMON_ASPECT_RATIOS } from '../../../../../../config/layoutConstants';
import { useDispatch, useSelector } from "react-redux";
import { setSelectedArea, setScreenDetails } from "../../../../../../redux/Slices/layoutSlice";
import { toggleDialog } from "../../../../../../redux/Slices/Global/globalSlice";
import AreaContentPreview from "./AreaContentPreview";
import AreaSettingsToggles from "./AreaSettingsToggles";

const AreaControls = ({
    areas,
    currentAspectRatio,
    setAreas,
    setOverlappingAreas,
    setIsSaveDisabled,
    scrollableDivRef,
    layoutColor = [],
    isDarkMode = false,
    sidebarWidth = 384,
    toolsState = null
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch()
    const queryClient = useQueryClient();
    const { unit, selectedArea, screens_setting } = useSelector((state) => state.layout);
    const openDialog = useSelector((state) => state.global.openDialog);


    const { control, setValue, setError, clearErrors, formState: { errors } } = useForm();
    const [isDuplicatedNames, setIsDuplicatedNames] = useState(false);

    useEffect(() => {
        // Sync React Hook values with the area state
        areas.forEach((area, index) => {
            setValue(`areas[${index}].start_margin`, area.start_margin);
            setValue(`areas[${index}].top_margin`, area.top_margin);
            setValue(`areas[${index}].width`, area.width);
            setValue(`areas[${index}].height`, area.height);
            if (area.name) {
                setValue(`areas[${index}].name`, area.name || `Screen ${area.order ?? index + 1}`);
            }
        });
    }, [areas, setValue]);

    const handleSmartInputChange = (id, field, value, unit, currentAspectRatio) => {
        setAreas((prevAreas) =>
            prevAreas.map((area) => {
                if (area.id === id) {
                    let updatedValue = parseFloat(value) || 0;
                    let maxWidth = 100;
                    let maxHeight = 100;

                    if (unit?.value === "px" && currentAspectRatio) {
                        const aspectKey = `${currentAspectRatio.width}:${currentAspectRatio.height}`;
                        if (COMMON_ASPECT_RATIOS[aspectKey]) {
                            maxWidth = COMMON_ASPECT_RATIOS[aspectKey].width;
                            maxHeight = COMMON_ASPECT_RATIOS[aspectKey].height;
                        } else {
                            maxWidth = currentAspectRatio.width;
                            maxHeight = currentAspectRatio.height;
                        }
                    }

                    if (unit.value === "%") {
                        updatedValue = Math.min(100, Math.max(0, updatedValue));
                    } else if (unit.value === "px") {
                        updatedValue = Math.min(field === "width" || field === "start_margin" ? maxWidth : maxHeight, Math.max(0, updatedValue));
                    }

                    if (field === "start_margin") {
                        if (area.width === maxWidth) {
                            const newWidth = Math.max(0, area.start_margin + area.width - updatedValue);
                            return {
                                ...area,
                                start_margin: updatedValue,
                                width: unit.value === "%" ? Math.min(100, newWidth) : Math.min(maxWidth - updatedValue, newWidth),
                            };
                        } else {
                            return {
                                ...area,
                                start_margin: updatedValue,
                            };
                        }
                    } else if (field === "width") {
                        const newStartMargin = Math.min(
                            unit.value === "%" ? 100 - updatedValue : maxWidth - updatedValue,
                            area.start_margin
                        );
                        return {
                            ...area,
                            width: updatedValue,
                            start_margin: newStartMargin,
                        };
                    } else if (field === "top_margin") {
                        if (area.height === maxHeight) {
                            const newHeight = Math.max(0, area.top_margin + area.height - updatedValue);
                            return {
                                ...area,
                                top_margin: updatedValue,
                                height: unit.value === "%" ? Math.min(100, newHeight) : Math.min(maxHeight, newHeight),
                            };
                        } else {
                            return {
                                ...area,
                                top_margin: updatedValue,
                            };
                        }
                    } else if (field === "height") {
                        const newTopMargin = Math.min(
                            unit.value === "%" ? 100 - updatedValue : maxHeight - updatedValue,
                            area.top_margin
                        );
                        return {
                            ...area,
                            height: updatedValue,
                            top_margin: newTopMargin,
                        };
                    }
                }
                return area;
            })
        );
    };


    const handleDeleteArea = (id) => {
        const updatedAreas = areas.filter((area) => area.id !== id);
        setAreas(updatedAreas);
    };

    const checkDuplicateNames = () => {
        const names = areas.map((area) => area.name);
        const duplicates = names.filter((name, index) => names.indexOf(name) !== index);
        if (duplicates.length > 0) {
            setError("duplicateNames", {
                type: "manual",
                message: `Duplicate names found: ${[...new Set(duplicates)].join(", ")}`,

            });
            return true

        } else {
            clearErrors("duplicateNames");
            return false
        }
    };

    const checkOverlap = () => {
        const overlappingAreas = [];
        for (let i = 0; i < areas.length; i++) {
            for (let j = i + 1; j < areas.length; j++) {
                const a = areas[i];
                const b = areas[j];
                const isOverlapping =
                    a.start_margin < b.start_margin + b.width &&
                    a.start_margin + a.width > b.start_margin &&
                    a.top_margin < b.top_margin + b.height &&
                    a.top_margin + a.height > b.top_margin;

                if (isOverlapping) {
                    overlappingAreas.push(i, j)
                }
            }
        }
        return Array.from(new Set(overlappingAreas));
    };


    useEffect(() => {
        const hasDuplicateNames = checkDuplicateNames();

        setIsDuplicatedNames(hasDuplicateNames);
        // Overlapping is allowed — always clear any overlap indicators
        setOverlappingAreas([]);

        // Filter valid areas (exclude temporary ones with no ID)
        const validAreas = areas.filter(item => item?.id);
        const hasSameLength = screens_setting.length === validAreas.length;

        const screenIds = screens_setting.map(item => item.id).sort();
        const areaIds = validAreas.map(item => item.id).sort();
        const hasSameIds = screenIds.every((id, index) => id === areaIds[index]);

        let hasDifferentFieldValues = false;

        if (hasSameLength && hasSameIds) {
            // Compare width, height, and margins for each matching ID
            for (let id of screenIds) {
                const original = screens_setting.find(item => item.id === id);
                const current = validAreas.find(item => item.id === id);

                const isDifferent =
                    original.width !== current.width ||
                    original.height !== current.height ||
                    original.start_margin !== current.start_margin ||
                    original.settings.live.order !== current.order ||
                    original.top_margin !== current.top_margin ||
                    // Add checks for global toggles
                    (original.settings?.live?.is_muted != current.settings?.live?.is_muted) ||
                    (original.settings?.live?.maintain_aspect_ratio != current.settings?.live?.maintain_aspect_ratio);

                if (isDifferent) {
                    hasDifferentFieldValues = true;
                    break;
                }
            }
        }

        // تعطيل الحفظ إذا:
        // 1. وجود أسماء مكررة بين الشاشات
        // 2. لا توجد تغييرات فعلية
        if (hasDuplicateNames) {
            setIsSaveDisabled(true);
            return;
        }

        const hasAnyChanges =
            !hasSameLength ||
            !hasSameIds ||
            hasDifferentFieldValues;

        setIsSaveDisabled(!hasAnyChanges);
    }, [areas]);


    const moveArea = (index, direction) => {
        const newAreas = [...areas];

        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= newAreas.length) return;

        // Swap the areas in the array
        const temp = newAreas[index];
        newAreas[index] = newAreas[targetIndex];
        newAreas[targetIndex] = temp;
        
        // CRITICAL FIX: Swap the order properties to reflect the new stacking
        const tempOrder = newAreas[index].order;
        const tempLiveOrder = newAreas[index].settings?.live?.order;

        newAreas[index] = { 
            ...newAreas[index], 
            order: newAreas[targetIndex].order,
            settings: {
                ...newAreas[index].settings,
                live: {
                    ...(newAreas[index].settings?.live || {}),
                    order: newAreas[targetIndex].settings?.live?.order ?? newAreas[targetIndex].order
                }
            }
        };

        newAreas[targetIndex] = { 
            ...newAreas[targetIndex], 
            order: tempOrder,
            settings: {
                ...newAreas[targetIndex].settings,
                live: {
                    ...(newAreas[targetIndex].settings?.live || {}),
                    order: tempLiveOrder ?? tempOrder
                }
            }
        };

        setAreas(newAreas);
    };

    const handleSettingChange = (index, key, value) => {
        setAreas(prevAreas => {
            const updatedAreas = prevAreas.map((area, i) => {
                if (i === index) {
                    // Return a new object for the updated area
                    return {
                        ...area,
                        settings: {
                            ...area.settings,
                            live: {
                                ...(area.settings?.live || {}),
                                [key]: value
                            }
                        }
                    };
                }
                return area;
            });
            return updatedAreas;
        });
    };


    // rgb(161 226 254 / 80%)
    return (
        <div className={`overflow-auto scroll_div space-y-4 p-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']
        ${openDialog.CreateAnimationLayout ? "max-h-[300px]" : "max-h-[52vh]"}  `} ref={scrollableDivRef}>
            {errors.duplicateNames && (
                <div className="text-red-500 text-sm mt-2">{errors.duplicateNames.message}</div>
            )}
            {/* {areas.map((area, index) => (
                <div */}
            {areas
                .slice()
                .map((area, index) => (
                    <div
                        key={area.id}
                        id={`area_${area.id}`}
                        className={`group transition-all duration-300 rounded-xl border-2 ${
                            selectedArea?.id === area.id 
                            ? (isDarkMode ? 'border-purple-500 shadow-[0_0_30px_rgba(168,85,247,0.6)] ring-1 ring-purple-500/50 bg-slate-800 z-10 scale-[1.02]' : 'border-[#8a8aed] shadow-[0_8px_30px_rgba(138,138,237,0.4)] ring-1 ring-[#8a8aed]/50 bg-white z-10 scale-[1.02]') 
                            : (isDarkMode ? 'border-slate-700 hover:border-purple-500/50 bg-slate-900 hover:bg-slate-800 hover:shadow-md' : 'border-gray-200 hover:border-[#8a8aed]/50 bg-gray-50/50 hover:bg-white hover:shadow-md')
                        }`}
                        onClick={() => dispatch(setSelectedArea(area))}
                    >
                        {/* Area Header */}
                        <div className={`p-3 border-b flex items-center justify-between transition-colors ${
                            selectedArea?.id === area.id 
                            ? (isDarkMode ? 'bg-purple-900/30 border-purple-500/40' : 'bg-[#8a8aed]/20 border-[#8a8aed]/40')
                            : (isDarkMode ? 'bg-transparent border-slate-700' : 'bg-transparent border-gray-100')
                        }`}>
                           <div className="flex items-center gap-3 flex-1 overflow-hidden">
                                <div 
                                    className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold shadow-sm shrink-0"
                                    style={{ 
                                        backgroundColor: toolsState?.areaColors?.[area.id] || ((layoutColor && layoutColor.length > 0) ? layoutColor[index % layoutColor.length] : '#ccc') 
                                    }}
                                    title={`Area #${area.order ?? index + 1}`}
                                >
                                    {area.order ?? index + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                     <Controller
                                        name={`areas[${index}].name`}
                                        control={control}
                                        defaultValue={area.name || `Screen ${area.order ?? index + 1}`}
                                        rules={{
                                            required: t('layouts.designer_required'),
                                            minLength: { value: 3, message: t('layouts.designer_too_short') },
                                            maxLength: { value: 35, message: t('layouts.designer_too_long') }
                                        }}
                                        render={({ field, fieldState }) => (
                                            <div className="flex flex-col">
                                                <InputText
                                                    {...field}
                                                    className={`w-full text-sm font-semibold bg-transparent border-none p-0 focus:ring-0 transition-colors rounded px-1 ${
                                                        isDarkMode 
                                                        ? 'text-gray-200 placeholder-gray-600 hover:text-indigo-400 focus:bg-slate-700' 
                                                        : 'text-gray-700 placeholder-gray-400 hover:text-indigo-600 focus:bg-white/50'
                                                    }`}
                                                    placeholder={`Zone ${area.order ?? index + 1}`}
                                                    onFocus={() => dispatch(setSelectedArea(area))}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value.length > 35) return;
                                                        field.onChange(value);
                                                        setValue(`areas[${index}].name`, value);
                                                        setAreas((prevAreas) =>
                                                            prevAreas.map((a, idx) =>
                                                                idx === index ? { ...a, name: value } : a
                                                            )
                                                        );
                                                        checkDuplicateNames();
                                                    }}
                                                />
                                                {fieldState.error && <span className="text-[10px] text-red-500 font-medium leading-none mt-0.5">{fieldState.error.message}</span>}
                                                {errors.duplicateNames && <span className="text-[10px] text-red-500 font-medium leading-none mt-0.5">{errors.duplicateNames.message}</span>}
                                            </div>
                                        )}
                                    />
                                </div>
                           </div>
                           
                           
                           <div className={`flex items-center gap-1 border rounded-lg p-1 shadow-sm transition-all duration-200 ${
                               isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-slate-600' : 'bg-white border-gray-100/50 hover:border-gray-200'
                           }`}>
                                <button 
                                    className={`w-7 h-7 flex items-center justify-center rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
                                        isDarkMode ? 'text-slate-400 hover:text-indigo-400 hover:bg-indigo-900/30' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'
                                    }`}
                                    onClick={(e) => { e.stopPropagation(); moveArea(index, 'up'); }}
                                    title={t('layouts.designer_move_up')}
                                >
                                    <i className="fa-solid fa-chevron-up text-[10px]"></i>
                                </button>
                                <div className={`w-px h-4 mx-0.5 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}></div>
                                <button 
                                    className={`w-7 h-7 flex items-center justify-center rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
                                        isDarkMode ? 'text-slate-400 hover:text-indigo-400 hover:bg-indigo-900/30' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'
                                    }`}
                                    onClick={(e) => { e.stopPropagation(); moveArea(index, 'down'); }}
                                    title={t('layouts.designer_move_down')}
                                >
                                    <i className="fa-solid fa-chevron-down text-[10px]"></i>
                                </button>
                                <div className={`w-px h-4 mx-0.5 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}></div>
                                <button 
                                    className={`w-7 h-7 flex items-center justify-center rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500/20 ${
                                        isDarkMode ? 'text-slate-400 hover:text-red-400 hover:bg-red-900/30' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                                    }`}
                                    onClick={(e) => { e.stopPropagation(); handleDeleteArea(area.id); }} 
                                    title={t('layouts.designer_delete_zone')}
                                >
                                    <i className="fa-solid fa-trash text-[10px]"></i>
                                </button>
                           </div>
                        </div>

                        {/* Area Body */}
                        <div className={`p-3 rounded-b-xl backdrop-blur-[2px] ${
                            isDarkMode ? 'bg-slate-800/50' : 'bg-white/50'
                        }`}>
                            {/* Position Grid */}
                            <div className="grid grid-cols-2 gap-2 mb-3">
                                {[
                                    { label: t('layouts.designer_pos_left'), name: 'start_margin', max: 100 },
                                    { label: t('layouts.designer_pos_top'), name: 'top_margin', max: 100 },
                                    { label: t('layouts.designer_pos_width'), name: 'width', max: 100 },
                                    { label: t('layouts.designer_pos_height'), name: 'height', max: 100 }
                                ].map((input) => (
                                    <div key={input.name} className={`rounded-lg py-1.5 px-2 border flex flex-col items-center transition-colors group/input focus-within:ring-1 focus-within:ring-indigo-400/20 ${
                                        isDarkMode 
                                        ? 'bg-slate-900 border-slate-700 hover:border-slate-600 focus-within:border-indigo-500/50' 
                                        : 'bg-gray-50 border-gray-100 hover:border-gray-300 focus-within:border-indigo-400'
                                    }`}>
                                         <label className={`text-[9px] uppercase font-bold mb-0.5 w-full text-center tracking-wider ${
                                             isDarkMode ? 'text-gray-500' : 'text-gray-400'
                                         }`}>{input.label}</label>
                                         <Controller
                                            name={`areas[${index}].${input.name}`}
                                            control={control}
                                            defaultValue={area[input.name]}
                                            render={({ field }) => (
                                                <InputNumber
                                                    {...field}
                                                    inputClassName={`text-center font-bold bg-transparent border-none w-full p-0 text-sm focus:ring-0 group-focus-within/input:text-indigo-500 ${
                                                        isDarkMode ? 'text-gray-200' : 'text-gray-700'
                                                    }`}
                                                    className="w-full border-none shadow-none h-6"
                                                    minFractionDigits={unit.value == "%" ? 1 : 0}
                                                    maxFractionDigits={unit.value == "%" ? 2 : 0}
                                                    value={field.value}
                                                    onFocus={() => dispatch(setSelectedArea(area))}
                                                    onChange={(e) => {
                                                        const value = e.value;
                                                        setValue(`areas[${index}].${input.name}`, value);
                                                        handleSmartInputChange(area.id, input.name, value, unit, currentAspectRatio);
                                                    }}
                                                />
                                            )}
                                        />
                                    </div>
                                ))}
                            </div>

                                    <AreaSettingsToggles 
                                        area={area} 
                                        index={index} 
                                        isDarkMode={isDarkMode} 
                                        onSettingChange={handleSettingChange}
                                        queryClient={queryClient}
                                    />

                            
                            {/* Content */}
                            <div className={`pt-2 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                                <AreaContentPreview 
                                    areaId={area.id} 
                                    onAddContent={() => {
                                        dispatch(setScreenDetails(area));
                                        dispatch(toggleDialog("addContent"));
                                    }}
                                    isDarkMode={isDarkMode}
                                    isAreaMuted={!!area.settings?.live?.is_muted}
                                    onPublish={() => queryClient.invalidateQueries(["getCampaignLayerContent", area.id, "MAIN"])}
                                    isCompact={sidebarWidth < 450}
                                    showDuration={true}
                                />
                            </div>
                        </div>
                    </div>
                ))}
        </div>
    );
};

export default AreaControls;
